/**
 * Die Matching-Engine: welche Bankbuchung gehoert zu welchem geplanten Posten?
 *
 * Das ist die Frage, an der das ganze Tool haengt. Wird sie falsch beantwortet,
 * zaehlt die Prognose Betraege doppelt (Posten offen *und* Geld schon da) oder
 * gar nicht (Posten faelschlich als bezahlt markiert).
 *
 * Der Ansatz ist bewusst nicht "ein cleverer Algorithmus", sondern eine Kette
 * von Verfahren mit absteigender Sicherheit. Was oben sicher zugeordnet wird,
 * laeuft ohne Rueckfrage durch; was unten unsicher bleibt, landet in der
 * Abgleich-Inbox. Jede Bestaetigung dort verbessert die oberen Stufen.
 *
 *   1. reference       Rechnungsnummer im Verwendungszweck            → bis 100
 *   2. rule            benutzerdefinierte Regel (Fixkosten)           → bis  96
 *   3. amountDateParty Betrag + Zeitfenster + Gegenpartei (+ IBAN)    → bis  98
 *   4. collective      eine Ueberweisung deckt mehrere Posten         → bis  98
 *   5. partial         Teilzahlung auf einen Posten                   → bis  92
 */
import { daysBetween } from "./dates.js";
import { nameSimilarity, normalizeIban, normalizeName, referenceOccursIn } from "./normalize.js";
import { AUTO_MATCH_THRESHOLD, openCents, unmatchedCents, } from "./types.js";
/** Unterhalb dieser Konfidenz ist ein Vorschlag mehr Rauschen als Hilfe. */
const NOISE_FLOOR = 45;
/**
 * Obergrenze fuer rein heuristische Treffer (Betrag + Datum + Gegenpartei).
 *
 * Auch ein in jedem Merkmal perfekter Treffer bleibt ein Indizienbeweis: bei
 * zwei gleich hohen Rechnungen desselben Kunden am selben Tag ist er schlicht
 * mehrdeutig. Eine Rechnungsnummer im Verwendungszweck ist dagegen ein
 * direkter Beleg. Die Deckelung stellt sicher, dass der Beleg den Indizien
 * immer vorgeht — sonst entscheidet bei Gleichstand die Reihenfolge im Array.
 */
const HEURISTIC_CEILING = 98;
/** Wie viele Posten eine einzelne Sammelueberweisung hoechstens erklaeren darf. */
const MAX_COLLECTIVE_ITEMS = 6;
/**
 * Wie lange vor dem Rechnungsdatum eine Zahlung noch plausibel ist.
 *
 * Vorkasse gibt es, aber eine Buchung Wochen vor der Rechnung deutet auf einen
 * Falschtreffer hin. Nur relevant, wenn keine Rechnungsnummer im Spiel ist —
 * die schlaegt jede Datumsplausibilitaet.
 */
const PREPAYMENT_GRACE_DAYS = 7;
/**
 * Groesster Abstand, den eine Zuordnung ohne Belegnummer haben darf.
 *
 * Wiederkehrende Rechnungen sind der Grund: eine Hosting-Rechnung ueber 21,42 EUR
 * gleicht jeder anderen ueber 21,42 EUR, und die Gegenpartei ist immer dieselbe.
 * Betrag und Name allein paaren dann eine Rechnung von September 2025 mit einer
 * Zahlung von Juni 2026 — 280 Tage auseinander, und formal gleich gut wie die
 * richtige. Ohne harte Grenze kreuzt jede Rechnung mit jedem Monat.
 *
 * Eine Rechnungsnummer im Verwendungszweck hebt die Grenze auf: die ist
 * eindeutig, egal wie lange es her ist.
 */
const MAX_DISTANCE_DAYS = 150;
// ---- Einstiegspunkt ----------------------------------------------------------
/**
 * Erzeugt Zuordnungsvorschlaege fuer alle noch offenen Bankbuchungen.
 *
 * Die Zuordnung selbst passiert am Ende global und gierig: alle Kandidaten
 * werden nach Konfidenz sortiert und der Reihe nach vergeben, solange auf
 * beiden Seiten noch Betrag uebrig ist. Damit kann ein Posten nicht zweimal
 * verplant werden, und der jeweils beste Vorschlag gewinnt — auch wenn er
 * erst weiter unten in der Liste auftaucht.
 */
export function proposeMatches(ctx) {
    const profiles = ctx.profiles ?? [];
    const rules = (ctx.rules ?? []).filter((r) => r.enabled);
    const openItems = ctx.plannedItems.filter((i) => openCents(i) > 0);
    const openTx = ctx.bankTransactions.filter((t) => unmatchedCents(t) > 0);
    const candidates = [];
    for (const tx of openTx) {
        const direction = tx.amountCents < 0 ? "out" : "in";
        const plausible = openItems.filter((i) => i.direction === direction);
        for (const item of plausible) {
            const scored = scorePair(item, tx, profiles, rules);
            if (scored && scored.confidence >= NOISE_FLOOR)
                candidates.push(scored);
        }
        // Sammelueberweisungen erst pruefen, wenn kein einzelner Posten den Betrag
        // exakt erklaert — sonst zerlegen wir eine saubere 1:1-Zahlung unnoetig.
        const exactSingle = plausible.some((i) => openCents(i) === unmatchedCents(tx));
        if (!exactSingle) {
            candidates.push(...proposeCollective(tx, plausible, profiles));
        }
    }
    return assignGreedily(candidates, openItems, openTx);
}
/** Vorschlaege, die ohne Rueckfrage gebucht werden duerfen. */
export function isAutoMatch(candidate) {
    return candidate.confidence >= AUTO_MATCH_THRESHOLD;
}
// ---- Stufe 1-3 und 5: Bewertung eines einzelnen Paares -----------------------
function scorePair(item, tx, profiles, rules) {
    const itemOpen = openCents(item);
    const txOpen = unmatchedCents(tx);
    const amount = Math.min(itemOpen, txOpen);
    if (amount <= 0)
        return null;
    const haystack = [tx.remittanceInformation, tx.referenceNumber].filter(Boolean).join(" ");
    const hasReference = referenceOccursIn(item.reference, haystack);
    // ---- Stufe 1: Rechnungsnummer -------------------------------------------
    if (hasReference) {
        const reasons = [`Referenz ${item.reference} im Verwendungszweck`];
        if (txOpen === itemOpen) {
            return candidate(item, tx, amount, "reference", 100, [...reasons, "Betrag stimmt exakt"]);
        }
        if (txOpen < itemOpen) {
            return candidate(item, tx, amount, "partial", 92, [
                ...reasons,
                `Teilzahlung: ${txOpen} von ${itemOpen} Cent`,
            ]);
        }
        // Buchung groesser als der Posten — vermutlich eine Sammelzahlung, die
        // Stufe 4 sauberer aufloest. Hier nur als Rueckfallebene anbieten.
        return candidate(item, tx, amount, "reference", 84, [
            ...reasons,
            "Buchungsbetrag groesser als der Posten — moeglicherweise Sammelzahlung",
        ]);
    }
    // ---- Stufe 2: benutzerdefinierte Regel ----------------------------------
    const rule = matchingRule(rules, item, tx);
    if (rule) {
        const tolerance = Math.abs(itemOpen * (rule.amountTolerancePercent / 100));
        const delta = Math.abs(txOpen - itemOpen);
        if (delta <= tolerance) {
            return candidate(item, tx, amount, "rule", 96, [
                `Regel "${rule.label}" greift`,
                delta === 0 ? "Betrag stimmt exakt" : `Betrag weicht um ${delta} Cent ab (erlaubt: ${Math.round(tolerance)})`,
            ]);
        }
    }
    // ---- Stufe 3: Betrag + Zeitfenster + Gegenpartei ------------------------
    const reasons = [];
    let score = 0;
    const delta = Math.abs(txOpen - itemOpen);
    if (delta === 0) {
        score += 50;
        reasons.push("Betrag stimmt exakt");
    }
    else if (delta <= 2) {
        score += 46;
        reasons.push(`Betrag weicht um ${delta} Cent ab (Rundung)`);
    }
    else if (delta <= itemOpen * 0.01) {
        score += 28;
        reasons.push(`Betrag weicht um ${(delta / 100).toFixed(2)} € ab`);
    }
    else if (txOpen < itemOpen) {
        // Deutlich kleiner: nur als Teilzahlung sinnvoll, und die braucht ein
        // starkes zweites Signal — sonst passt jede Kleinbuchung auf jede Rechnung.
        score += 8;
        reasons.push("moegliche Teilzahlung");
    }
    else {
        return null;
    }
    const dayDelta = daysBetween(item.expectedDate, tx.bookingDate);
    const absDays = Math.abs(dayDelta);
    // Ohne Beleg als Anker ist ein solcher Abstand kein schwacher Treffer,
    // sondern keiner.
    if (absDays > MAX_DISTANCE_DAYS)
        return null;
    if (absDays <= 7) {
        score += 25;
        reasons.push(dayDelta === 0 ? "am erwarteten Tag gebucht" : `${absDays} Tage vom erwarteten Termin`);
    }
    else if (absDays <= 30) {
        score += 18;
        reasons.push(`${absDays} Tage vom erwarteten Termin`);
    }
    else if (absDays <= 60) {
        score += 10;
        reasons.push(`${absDays} Tage vom erwarteten Termin`);
    }
    else if (absDays <= 120) {
        score += 3;
        reasons.push(`${absDays} Tage vom erwarteten Termin`);
    }
    // Zahlung deutlich vor Rechnungsdatum ist meist ein Falschtreffer.
    if (daysBetween(item.documentDate, tx.bookingDate) < -PREPAYMENT_GRACE_DAYS) {
        score -= 35;
        reasons.push("Buchung liegt vor dem Rechnungsdatum");
    }
    const similarity = nameSimilarity(item.counterparty, tx.counterpartyName);
    if (similarity >= 0.9) {
        score += 25;
        reasons.push("Gegenpartei stimmt ueberein");
    }
    else if (similarity >= 0.75) {
        score += 18;
        reasons.push("Gegenpartei sehr aehnlich");
    }
    else if (similarity >= 0.55) {
        score += 8;
        reasons.push("Gegenpartei aehnlich");
    }
    if (ibanKnownFor(item, tx, profiles)) {
        score += 20;
        reasons.push("IBAN ist fuer diese Gegenpartei bekannt");
    }
    const confidence = Math.min(clamp(score), HEURISTIC_CEILING);
    if (confidence < NOISE_FLOOR)
        return null;
    const tier = txOpen < itemOpen ? "partial" : "amountDateParty";
    return candidate(item, tx, amount, tier, confidence, reasons);
}
// ---- Stufe 4: Sammelueberweisungen ------------------------------------------
/**
 * Sucht eine Kombination offener Posten derselben Gegenpartei, deren Summe die
 * Buchung exakt erklaert.
 *
 * Kunden begleichen regelmaessig drei Rechnungen mit einer Ueberweisung. Ohne
 * diesen Schritt bliebe die Buchung ungeklaert und alle drei Rechnungen
 * stuenden weiter als offen in der Prognose.
 */
function proposeCollective(tx, items, profiles) {
    const target = unmatchedCents(tx);
    if (target <= 0)
        return [];
    // Nur Posten, die plausibel zur Gegenpartei der Buchung gehoeren.
    const related = items.filter((i) => nameSimilarity(i.counterparty, tx.counterpartyName) >= 0.75 || ibanKnownFor(i, tx, profiles));
    if (related.length < 2)
        return [];
    const subset = findSubset(related, target);
    if (!subset || subset.length < 2)
        return [];
    const haystack = [tx.remittanceInformation, tx.referenceNumber].filter(Boolean).join(" ");
    const allReferenced = subset.every((i) => referenceOccursIn(i.reference, haystack));
    let confidence = 80;
    const reasons = [`Sammelzahlung ueber ${subset.length} Posten, Summe stimmt exakt`];
    if (allReferenced) {
        confidence = 98;
        reasons.push("alle Rechnungsnummern im Verwendungszweck");
    }
    if (subset.some((i) => ibanKnownFor(i, tx, profiles))) {
        confidence = clamp(confidence + 6);
        reasons.push("IBAN ist fuer diese Gegenpartei bekannt");
    }
    const ids = subset.map((i) => i.id);
    return subset.map((item) => ({
        plannedItemId: item.id,
        bankTransactionId: tx.id,
        amountCents: openCents(item),
        tier: "collective",
        confidence,
        reasons,
        groupWith: ids.filter((id) => id !== item.id),
    }));
}
/**
 * Teilmengensuche mit exakter Summe.
 *
 * Absteigend sortiert plus Abbruch bei Ueberschreitung; bei der hier
 * realistischen Groessenordnung (eine Handvoll offener Posten pro Kunde) ist
 * das exakt und schnell genug. `MAX_COLLECTIVE_ITEMS` deckelt die Tiefe, damit
 * ein Kunde mit 40 offenen Posten die Suche nicht sprengt.
 */
function findSubset(items, target) {
    const sorted = [...items].sort((a, b) => openCents(b) - openCents(a));
    const picked = [];
    function dfs(start, remaining) {
        if (remaining === 0)
            return picked.length >= 2;
        if (picked.length >= MAX_COLLECTIVE_ITEMS || start >= sorted.length)
            return false;
        for (let i = start; i < sorted.length; i++) {
            const value = openCents(sorted[i]);
            if (value > remaining)
                continue; // absteigend sortiert, aber Gleichstaende ueberspringen
            picked.push(sorted[i]);
            if (dfs(i + 1, remaining - value))
                return true;
            picked.pop();
        }
        return false;
    }
    return dfs(0, target) ? [...picked] : null;
}
// ---- Globale Zuteilung -------------------------------------------------------
/**
 * Vergibt die Kandidaten gierig nach Konfidenz und respektiert dabei die
 * verbleibende Kapazitaet auf beiden Seiten.
 *
 * Gierig statt optimal, weil das Ergebnis nachvollziehbar sein muss: der
 * sicherste Vorschlag gewinnt. Eine global optimale Zuordnung koennte dagegen
 * einen 100%-Treffer aufloesen, um zwei mittelmaessige zu ermoeglichen — im
 * Zweifel gehoert so etwas in die Inbox, nicht in eine Optimierung.
 */
function assignGreedily(candidates, items, transactions) {
    const itemCapacity = new Map(items.map((i) => [i.id, openCents(i)]));
    const txCapacity = new Map(transactions.map((t) => [t.id, unmatchedCents(t)]));
    // Beweiskraft der Stufen, falls die Konfidenz gleich ist: ein expliziter
    // Beleg schlaegt eine Regel, eine Regel schlaegt reine Indizien.
    const tierRank = {
        reference: 5, rule: 4, collective: 3, amountDateParty: 2, partial: 1, manual: 0,
    };
    const ranked = [...candidates].sort((a, b) => {
        if (b.confidence !== a.confidence)
            return b.confidence - a.confidence;
        if (tierRank[b.tier] !== tierRank[a.tier])
            return tierRank[b.tier] - tierRank[a.tier];
        // Bei Gleichstand gewinnt der groessere Betrag — der erklaert mehr.
        return b.amountCents - a.amountCents;
    });
    const accepted = [];
    const seen = new Set();
    for (const c of ranked) {
        const key = `${c.plannedItemId}:${c.bankTransactionId}`;
        if (seen.has(key))
            continue;
        const itemLeft = itemCapacity.get(c.plannedItemId) ?? 0;
        const txLeft = txCapacity.get(c.bankTransactionId) ?? 0;
        const amount = Math.min(c.amountCents, itemLeft, txLeft);
        if (amount <= 0)
            continue;
        seen.add(key);
        itemCapacity.set(c.plannedItemId, itemLeft - amount);
        txCapacity.set(c.bankTransactionId, txLeft - amount);
        accepted.push({ ...c, amountCents: amount });
    }
    return accepted;
}
// ---- Hilfsfunktionen ---------------------------------------------------------
function candidate(item, tx, amountCents, tier, confidence, reasons) {
    return {
        plannedItemId: item.id,
        bankTransactionId: tx.id,
        amountCents,
        tier,
        confidence: clamp(confidence),
        reasons,
    };
}
function clamp(score) {
    return Math.max(0, Math.min(100, Math.round(score)));
}
function ibanKnownFor(item, tx, profiles) {
    const txIban = normalizeIban(tx.counterpartyIban);
    if (!txIban)
        return false;
    if (normalizeIban(item.counterpartyIban) === txIban)
        return true;
    const name = normalizeName(item.counterparty);
    for (const p of profiles) {
        const belongs = (item.counterpartyId !== null && p.companyId === item.counterpartyId) ||
            normalizeName(p.displayName) === name ||
            p.aliases.some((a) => normalizeName(a) === name);
        if (belongs && p.ibans.some((i) => normalizeIban(i) === txIban))
            return true;
    }
    return false;
}
function matchingRule(rules, item, tx) {
    for (const rule of rules) {
        if (rule.targetSource !== item.source)
            continue;
        if (rule.targetSourceKey && !item.sourceId.startsWith(rule.targetSourceKey))
            continue;
        if (rule.iban) {
            if (normalizeIban(rule.iban) !== normalizeIban(tx.counterpartyIban))
                continue;
        }
        if (rule.remittancePattern && !safeRegexTest(rule.remittancePattern, tx.remittanceInformation))
            continue;
        if (rule.counterpartyPattern && !safeRegexTest(rule.counterpartyPattern, tx.counterpartyName))
            continue;
        // Eine Regel ohne jedes Kriterium wuerde auf alles passen.
        if (!rule.iban && !rule.remittancePattern && !rule.counterpartyPattern)
            continue;
        return rule;
    }
    return null;
}
/**
 * Wertet ein benutzerdefiniertes Muster aus.
 *
 * Die Regeln kommen aus dem UI, koennen also fehlerhaft sein. Ein ungueltiges
 * Muster darf den gesamten Abgleichlauf nicht abbrechen — es greift dann
 * einfach nicht.
 */
function safeRegexTest(pattern, value) {
    if (!value)
        return false;
    try {
        return new RegExp(pattern, "i").test(value);
    }
    catch {
        return false;
    }
}
/**
 * Untergrenze, ab der ein Vorschlag ueberhaupt in der Inbox auftaucht.
 * `AUTO_MATCH_THRESHOLD` kommt aus `types.ts` und wird hier nicht erneut
 * exportiert — ein zweiter Stern-Export desselben Namens wuerde ihn in
 * `index.ts` mehrdeutig machen und damit stillschweigend verschwinden lassen.
 */
export { NOISE_FLOOR as MATCH_NOISE_FLOOR };
//# sourceMappingURL=matching.js.map